import { t } from "@lingui/macro";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { formatStudentSchedule } from "@/client/libs/student-schedule";
import { useCallback, useEffect, useMemo, useState } from "react";
import ChipLabel from "@/client/components/custom/custom-chip";
import { useAppContext } from "@/client/hooks/context";
import { useUser } from "@/client/services/user";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SECRET_KEY = "JobPrep@2024#SecureKey!";

const xorCipher = (text: string, key: string) => {
  let result = "";
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return result;
};

const encodeParamsAsBase64 = (code: string, email: string) => {
  try {
    const params = `code=${code}&email=${email}`;
    const encryptedParams = xorCipher(params, SECRET_KEY);
    return encodeURIComponent(btoa(encryptedParams));
  } catch (error) {
    console.error("Error encoding parameters:", error);
    return `code=${code}&email=${email}`;
  }
};

// ─── Styled Components ────────────────────────────────────────────────────────

const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 2),
  backgroundColor: "#f7faff",
  borderRadius: "12px",
  border: "1px solid #dde8fb",
  marginBottom: theme.spacing(1.5),
}));

const filterSelectSx = {
  fontSize: "13px",
  bgcolor: "#fff",
  borderRadius: "8px",
  "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px 12px 0 0",
  border: "1px solid #eaf0fb",
  borderBottom: "none",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
  maxHeight: "65vh",
  overflowY: "auto",
}));

const StyledTableHead = styled(TableHead)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  "& .MuiTableCell-root": {
    backgroundColor: "#f7faff",
    fontWeight: 700,
    fontSize: "13px",
    color: "#374151",
    borderBottom: "2px solid #dde8fb",
    whiteSpace: "nowrap",
    padding: "10px 14px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "#f0f6ff" },
  "&:last-child .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableCell-root": {
    fontSize: "13px",
    color: "#374151",
    borderBottom: "1px solid #eaf0fb",
    padding: "10px 14px",
  },
}));

// ─── Status Chip ──────────────────────────────────────────────────────────────

const StatusChip = ({ status }: { status: string }) => {
  switch (status) {
    case "upcoming":
      return <ChipLabel label={t`Upcoming`} sx={{ color: "orange.main", backgroundColor: "orange.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    case "open":
      return <ChipLabel label={t`Open`} sx={{ color: "blue.main", backgroundColor: "blue.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    case "completed":
      return <ChipLabel label={t`Completed`} sx={{ color: "green.main", backgroundColor: "green.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    case "missed":
      return <ChipLabel label={t`Missed`} sx={{ color: "red.main", backgroundColor: "red.light", fontSize: "11px", p: "0px", height: "24px" }} />;
    default:
      return null;
  }
};

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ─── Main Component ───────────────────────────────────────────────────────────

const CodingPage = () => {
  const [data, setData] = useState<any[]>([]);
  const { user } = useUser();
  const { loading, setLoading, reloading } = useAppContext();
  const userId = user?.email;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/integration/external-service/coding`, {
        params: { email: userId },
      });
      setData(res.data.result || []);
    } catch (error) {
      console.error(error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [userId, reloading, setLoading]);

  useEffect(() => {
    if (userId) fetchData();
  }, [fetchData, userId]);

  useEffect(() => {
    setPage(0);
  }, [search, filterCategory, filterStatus]);

  const rows = useMemo(
    () =>
      data.map((item: any) => ({
        id: item.id,
        codingActivityId: item.codingActivityId,
        name: item.name,
        code: item.code,
        category: item.category === "practice" ? t`Practice` : t`Final`,
        rawCategory: item.category,
        questionCount: item.questionCount,
        score: item.score === null ? "--" : `${item.score}/${item.totalScore}`,
        startDateTime: formatStudentSchedule(item.startAt, item.startDate, item.startTime),
        endDateTime: formatStudentSchedule(item.endAt, item.endDate, item.endTime),
        currentStatus: item.status,
        isView: item.isView,
      })),
    [data]
  );

  const filteredRows = useMemo(() => {
    const query = search.toLowerCase().trim();
    return rows.filter((row) => {
      if (query && !row.name.toLowerCase().includes(query) && !String(row.code || "").toLowerCase().includes(query)) return false;
      if (filterCategory !== "All" && row.category !== filterCategory) return false;
      if (filterStatus !== "All" && row.currentStatus !== filterStatus) return false;
      return true;
    });
  }, [rows, search, filterCategory, filterStatus]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const categoryOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.category)))],
    [rows]
  );

  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.currentStatus)))],
    [rows]
  );

  const hasActiveFilters = search !== "" || filterCategory !== "All" || filterStatus !== "All";

  const clearFilters = () => {
    setSearch("");
    setFilterCategory("All");
    setFilterStatus("All");
  };

  return (
    <Box sx={{ flexGrow: 1, px: 1 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-end", mb: 1.5 }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            fontSize: "15px",
            fontWeight: 700,
            color: "primary.main",
            mb: "-2px",
            userSelect: "none",
          }}
        >
          {t`Coding`}
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar sx={{ flexWrap: "nowrap", alignItems: "center", gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "primary.main", whiteSpace: "nowrap", flexShrink: 0 }}>
          {t`Filters`}
        </Typography>

        <TextField
          size="small"
          placeholder={t`Search name, code...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
          }}
          sx={{
            flex: "1 1 140px",
            minWidth: 120,
            maxWidth: 180,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
            "& input:focus": { outline: "none !important", boxShadow: "none !important" },
          }}
        />

        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Category`}</InputLabel>
          <Select
            label={t`Category`}
            value={filterCategory}
            onChange={(e: SelectChangeEvent) => setFilterCategory(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
          >
            {categoryOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Status`}</InputLabel>
          <Select
            label={t`Status`}
            value={filterStatus}
            onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px", textTransform: "capitalize" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
              {hasActiveFilters
                ? `${filteredRows.length} of ${rows.length} codings`
                : `${rows.length} codings`}
            </Typography>
          )}
          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              onClick={clearFilters}
              sx={{
                fontSize: "11px",
                bgcolor: "#fff0f0",
                color: "#c62828",
                borderColor: "#ffcdd2",
                "& .MuiChip-deleteIcon": { color: "#c62828" },
              }}
              variant="outlined"
            />
          )}
        </Box>
      </FilterBar>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
          <CircularProgress size={36} />
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>{t`Loading codings...`}</Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {hasActiveFilters
              ? t`No codings match your filters. Try adjusting or clearing them.`
              : t`No codings found.`}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #eaf0fb",
            boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <StyledTableContainer>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 860 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "140px" }}>{t`Code`}</TableCell>
                  <TableCell sx={{ width: "150px" }}>{t`Coding Name`}</TableCell>
                  <TableCell sx={{ width: "90px" }}>{t`Category`}</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>{t`Questions`}</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>{t`Score`}</TableCell>
                  <TableCell align="center" sx={{ width: "120px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />{t`Start Time`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "120px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />{t`End Time`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>{t`Status`}</TableCell>
                  <TableCell align="center" sx={{ width: "110px" }}>{t`Actions`}</TableCell>
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {paginatedRows.map((row) => (
                  <StyledTableRow key={row.id}>
                    {/* Code */}
                    <TableCell sx={{ width: "140px", overflow: "hidden" }}>
                      <Chip
                        label={row.code}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          bgcolor: "#f0f4ff",
                          color: "#1976d2",
                          fontWeight: 600,
                          width: "130px",
                          minWidth: "130px",
                          justifyContent: "center",
                        }}
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Name */}
                    <TableCell sx={{ maxWidth: "180px", minWidth: "180px", width: "180px" }}>
                      <Tooltip title={row.name} placement="top">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "primary.main",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Category */}
                    <TableCell sx={{ width: "90px", overflow: "hidden" }}>
                      <Chip
                        label={row.category}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          width: "72px",
                          minWidth: "72px",
                          justifyContent: "center",
                          bgcolor: row.rawCategory === "practice" ? "#f0fff4" : "#fff8e1",
                          color: row.rawCategory === "practice" ? "#388e3c" : "#f57f17",
                        }}
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Questions */}
                    <TableCell align="center" sx={{ width: "80px" }}>
                      <Typography sx={{ fontSize: "13px" }}>{row.questionCount}</Typography>
                    </TableCell>

                    {/* Score */}
                    <TableCell align="center" sx={{ width: "80px" }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: row.score !== "--" ? 600 : 400 }}>
                        {row.score}
                      </Typography>
                    </TableCell>

                    {/* Start Time */}
                    <TableCell align="center" sx={{ width: "120px" }}>
                      <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                        {row.startDateTime}
                      </Typography>
                    </TableCell>

                    {/* End Time */}
                    <TableCell align="center" sx={{ width: "120px" }}>
                      <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                        {row.endDateTime}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center" sx={{ width: "90px" }}>
                      <StatusChip status={row.currentStatus} />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center" sx={{ width: "110px" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75 }}>
                        {row.currentStatus === "open" && (
                          <Tooltip title={t`Attend Coding`}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={{ textTransform: "none", fontSize: "11px", height: "26px", width: "100px", minWidth: "100px" }}
                            >
                              <a
                                href={`${import.meta.env.VITE_INTERVIEW_WEB_APP_URL}/coding?data=${encodeParamsAsBase64(row.code, userId || "")}`}
                                rel="noopener noreferrer"
                                style={{ color: "white", textDecoration: "none", display: "flex", alignItems: "center", gap: 3 }}
                              >
                                {t`Attend`}
                                <ArrowForwardIcon sx={{ fontSize: 12 }} />
                              </a>
                            </Button>
                          </Tooltip>
                        )}

                        {row.currentStatus === "completed" && (
                          <Tooltip title={t`View Result`}>
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              sx={{ textTransform: "none", fontSize: "11px", height: "26px", width: "100px", minWidth: "100px" }}
                            >
                              <a
                                href={`${import.meta.env.VITE_INTERVIEW_WEB_APP_URL}/coding/result?data=${encodeParamsAsBase64(row.code, userId || "")}`}
                                rel="noopener noreferrer"
                                style={{ color: "white", textDecoration: "none", display: "flex", alignItems: "center", gap: 3 }}
                              >
                                {t`Result`}
                                <VisibilityRoundedIcon sx={{ fontSize: 12 }} />
                              </a>
                            </Button>
                          </Tooltip>
                        )}

                        {(row.currentStatus === "upcoming" || row.currentStatus === "missed") && (
                          <Button
                            disabled
                            variant="contained"
                            size="small"
                            sx={{ textTransform: "none", fontSize: "11px", height: "26px", width: "100px", minWidth: "100px" }}
                          >
                            {row.currentStatus === "upcoming" ? t`Not Available` : t`Expired`}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #eaf0fb",
              bgcolor: "#f7faff",
              fontSize: "12px",
              "& .MuiTablePagination-toolbar": { minHeight: "40px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "12px" },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default CodingPage;
